import { FlexibleConnectedPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Directive,
  ElementRef, EventEmitter, HostListener,
  Input,
  OnDestroy, Output, TemplateRef,
  ViewContainerRef
} from '@angular/core';
import ResizeObserver from 'resize-observer-polyfill';
import { merge, Observable, Subscription } from 'rxjs';

export interface ICustomOverlay extends ElementRef {
  templateRef: TemplateRef<any>;
  readonly closed: EventEmitter<void>;
  overlayPositioningConfig: ICustomPositioningConfig;
}

export interface ICustomPositioningConfig {
  originX: 'start' | 'center' | 'end';
  originY: 'top' | 'center' | 'bottom';
  overlayX: 'start' | 'center' | 'end';
  overlayY: 'top' | 'center' | 'bottom';
  offsetX?: number;
  offsetY?: number;
}

@Directive({
  selector: '[appCustomOverlayTriggerFor]',
  standalone: true
})
export class CustomOverlayForDirective implements OnDestroy {
  private isDropdownOpen = false;
  private overlayRef: OverlayRef;
  private dropdownClosingActionsSub = Subscription.EMPTY;
  private componentResizeObserver: ResizeObserver;

  @Input('appCustomOverlayTriggerFor') public customElement: ICustomOverlay;
  @Input() shouldClose: () => boolean;
  @Input() disabled: boolean;
  @Output() closingEvent = new EventEmitter<void>();

  constructor(
    private overlay: Overlay,
    private elementRef: ElementRef<HTMLElement>,
    private viewContainerRef: ViewContainerRef
  ) {
  }

  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    if (event.code === 'Escape') {
      this.destroyDropdown();
    }
  }

  @HostListener('click') toggleDropdown(): void {
    this.isDropdownOpen ? this.destroyDropdown() : this.openDropdown();
  }

  openDropdown(): void {
    if (this.disabled) return;
    this.isDropdownOpen = true;
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      panelClass: 'cdk-overlay-custom-component-pane',
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      positionStrategy: this.setOverlayPositionStrategy()
    });

    const templatePortal = new TemplatePortal(
      this.customElement.templateRef,
      this.viewContainerRef
    );
    this.overlayRef.attach(templatePortal);

    this.componentResizeObserver = new ResizeObserver(entries => {
      const contentRect = entries[entries.length - 1].contentRect;

      this.overlayRef.updatePositionStrategy(this.setOverlayPositionStrategy(contentRect.width));
      this.overlayRef.updatePosition();
    });
    this.componentResizeObserver.observe(this.overlayRef.overlayElement);

    this.dropdownClosingActionsSub = this.dropdownClosingActions().subscribe(
      () => this.destroyDropdown()
    );
  }

  setOverlayPositionStrategy(width?: number): FlexibleConnectedPositionStrategy {
    const customElementWidth = width ? width : 280;
    let customElementXOffset;
    let overlayPositions;

    if (this.customElement.overlayPositioningConfig) {
      const config = this.customElement.overlayPositioningConfig;
      customElementXOffset = this.customElement.overlayPositioningConfig.offsetX;
      const resultOffsetX = (customElementWidth / 2) + customElementXOffset;
      overlayPositions = {
        ...config,
        offsetX: resultOffsetX
      };
    } else {
      const config = this.customElement.overlayPositioningConfig;
      customElementXOffset = 0;
      const resultOffsetX = (customElementWidth / 2) + customElementXOffset;
      overlayPositions = {
        ...config,
        offsetX: resultOffsetX
      };
    }

    return this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions([
        overlayPositions
      ]);
  }

  private dropdownClosingActions(): Observable<MouseEvent | {} | void> {
    const backdropClick$ = this.overlayRef.backdropClick();
    const detachment$ = this.overlayRef.detachments();
    const dropdownClosed = this.customElement.closed;

    return merge(backdropClick$, detachment$, dropdownClosed);
  }

  private destroyDropdown(): void {
    if (!this.overlayRef || !this.isDropdownOpen || !this.shouldClose()) {
      return;
    }

    this.dropdownClosingActionsSub.unsubscribe();
    this.isDropdownOpen = false;
    this.componentResizeObserver.unobserve(this.overlayRef.overlayElement);
    this.overlayRef.detach();

    this.closingEvent.emit();
  }

  ngOnDestroy(): void {
    if (this.overlayRef) {
      this.componentResizeObserver.unobserve(this.overlayRef.overlayElement);
      this.overlayRef.dispose();
    }
  }
}
